/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from './api';

const AuthContext = createContext(null);

/**
 * Cookie-based authentication provider.
 *
 * The backend issues an httpOnly `sn_auth` cookie on login/register and
 * clears it on logout. The browser attaches it automatically to every
 * XHR because `api` is configured with `withCredentials: true`.
 *
 * We no longer store the JWT in localStorage. A short legacy fallback is
 * kept in `api.js` interceptor for users mid-migration; removed here.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch (e) {
      // 401 is expected when not logged in — treat as anonymous, don't spam console
      if (e?.response?.status !== 401) {
        console.error('auth.loadMe failed', e);
      }
      // Clear legacy localStorage token if it's invalid (backend rejected it)
      try { localStorage.removeItem('sc_token'); } catch { /* noop */ }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  // Secure state check: se una chiamata API risponde 401 (sessione scaduta/revocata)
  // o la finestra torna in primo piano, riverifichiamo la sessione. Così, appena
  // l'admin esce o il cookie scade, `user` torna null e le protezioni si riattivano.
  useEffect(() => {
    const onUnauthorized = () => { setUser((prev) => (prev ? null : prev)); };
    const onFocus = () => { if (document.visibilityState === 'visible') loadMe(); };
    window.addEventListener('sn:unauthorized', onUnauthorized);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('sn:unauthorized', onUnauthorized);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [loadMe]);

  const login = async (email, password, otp_code) => {
    const res = await api.post('/auth/login', { email, password, otp_code });
    if (res.data.requires_2fa) return { requires_2fa: true };
    // Cookie is set by the backend Set-Cookie header — no localStorage needed.
    // Clean up any legacy token that may still be lingering.
    try { localStorage.removeItem('sc_token'); } catch { /* noop */ }
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (payload) => {
    const res = await api.post('/auth/register', payload);
    try { localStorage.removeItem('sc_token'); } catch { /* noop */ }
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      console.warn('logout endpoint failed (ignoring)', e);
    }
    try { localStorage.removeItem('sc_token'); } catch { /* noop */ }
    setUser(null);
  };

  const refreshUser = loadMe;

  const updateProfile = async (payload) => {
    const res = await api.put('/auth/me', payload);
    setUser(res.data);
    return res.data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser, updateProfile }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
